'use client'

import React from 'react'

import { OB, FONT_SANS, FONT_MONO, Icon, Sparkle, PaprMark } from './kit'
import { HeroProduct } from './landing'
import { FunnelStyles } from './styles'
import { SiteNav, SiteFooter } from './site-chrome'

/**
 * MarketingLanding — the production marketing landing page (rendered at `/`).
 *
 * Minimal ink/paper/acid aesthetic with a clear section rhythm:
 *  - alternating surface bands (warm beige `bg` / near-white `paper` / dark `ink`)
 *    so sections read as distinct, and
 *  - a dark mid-page CTA between the value sections.
 * Section headings are centered with a small uppercase eyebrow. Card sections sit
 * on beige (so the near-white cards pop); card-less sections take the contrasting
 * surfaces. CTAs link to the real auth routes.
 */

const MAXW = 1120

function scrollTo(id: string) {
  if (typeof document !== 'undefined') {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }
}

/** Full-width surface band with a centered max-width content column. */
function Band({
  surface = 'bg',
  id,
  pad = '64px 28px',
  children,
}: {
  surface?: 'bg' | 'paper' | 'ink'
  id?: string
  pad?: string
  children: React.ReactNode
}) {
  const bgMap = { bg: OB.bg, paper: OB.paper, ink: OB.ink }
  return (
    <section id={id} style={{ background: bgMap[surface] }}>
      <div style={{ maxWidth: MAXW, margin: '0 auto', padding: pad }}>{children}</div>
    </section>
  )
}

/** Centered eyebrow + heading (+ optional sub). `onDark` flips colors for ink bands. */
function SectionHeading({
  eyebrow,
  title,
  sub,
  onDark,
}: {
  eyebrow: string
  title: string
  sub?: string
  onDark?: boolean
}) {
  const accent = onDark ? OB.lime : OB.purple
  return (
    <div style={{ maxWidth: 640, margin: '0 auto 44px', textAlign: 'center' }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 16, fontSize: 11.5, fontWeight: 600, fontFamily: FONT_MONO, letterSpacing: '0.12em', textTransform: 'uppercase', color: accent }}>
        <Sparkle size={12} color={accent} /> {eyebrow}
      </div>
      <h2 style={{ fontSize: 34, fontWeight: 600, letterSpacing: '-0.03em', margin: 0, color: onDark ? '#fff' : OB.ink, lineHeight: 1.1 }}>{title}</h2>
      {sub && (
        <p style={{ fontSize: 16, color: onDark ? 'rgba(255,255,255,0.62)' : OB.mute, lineHeight: 1.55, margin: '14px auto 0', maxWidth: 520 }}>{sub}</p>
      )}
    </div>
  )
}

export function MarketingLanding() {

  return (
    <div className="papr-funnel" style={{ width: '100%', minHeight: '100vh', background: OB.bg, fontFamily: FONT_SANS, color: OB.ink }}>
      <FunnelStyles />

      {/* Nav */}
      <SiteNav />

      {/* Hero + metrics (warm beige) */}
      <section style={{ background: OB.bg }}>
        <div style={{ maxWidth: MAXW, margin: '0 auto', padding: '64px 28px 40px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 56, alignItems: 'center' }} className="onb-hero">
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 13px', borderRadius: 999, background: '#fff', border: `1px solid ${OB.line2}`, fontSize: 12.5, color: OB.mute, marginBottom: 24, whiteSpace: 'nowrap' }}>
              <Sparkle size={13} color={OB.purple} /> Meet your invoicing co-pilot
            </div>
            <h1 style={{ fontSize: 52, fontWeight: 600, letterSpacing: '-0.04em', lineHeight: 1.02, margin: 0 }}>
              Get paid,<br /><span style={{ position: 'relative', display: 'inline-block' }}>
                without the chasing.
                <span style={{ position: 'absolute', left: 0, right: 0, bottom: 4, height: 10, background: OB.lime, zIndex: -1, borderRadius: 3 }}></span>
              </span>
            </h1>
            <p style={{ fontSize: 17, color: OB.mute, lineHeight: 1.55, margin: '22px 0 30px', maxWidth: 460 }}>
              Papr drafts your invoices, sends them, and politely chases late payers — so cash lands on time and you never write “just following up” again.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <a href="/auth/register" className="onb-primary" style={{ height: 52, padding: '0 24px', borderRadius: 13, border: 'none', background: OB.lime, color: OB.ink, fontSize: 16, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 9, textDecoration: 'none' }}>
                Start for free <Icon d="M5 12h14M13 6l6 6-6 6" size={17} color={OB.ink} />
              </a>
              <button onClick={() => scrollTo('how-it-works')} className="onb-ghost" style={{ height: 52, padding: '0 20px', borderRadius: 13, border: `1px solid ${OB.line2}`, background: '#fff', color: OB.ink, fontSize: 15, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 9 }}>
                <span style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon d="M8 5v14l11-7z" size={11} color={OB.ink} /></span>
                See how it works
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 26 }}>
              <div style={{ display: 'flex' }}>
                {['#f59e0b', '#0ea5e9', '#7c5cff', '#10b981'].map((c, i) => (
                  <div key={i} style={{ width: 30, height: 30, borderRadius: '50%', background: c, border: `2px solid ${OB.bg}`, marginLeft: i ? -10 : 0 }}></div>
                ))}
              </div>
              <div style={{ fontSize: 13, color: OB.mute }}>
                <strong style={{ color: OB.ink, fontWeight: 600 }}>12,000+</strong> freelancers &amp; small businesses · <span style={{ fontFamily: FONT_MONO }}>★ 4.9</span>
              </div>
            </div>
          </div>
          <HeroProduct />
        </div>
        <div style={{ maxWidth: MAXW, margin: '0 auto', padding: '12px 28px 64px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }} className="onb-feat">
            {[
              { v: '2× faster', l: 'Average time to get paid' },
              { v: '−71%', l: 'Fewer overdue invoices' },
              { v: '8 hrs', l: 'Saved on admin each month' },
            ].map((m, i) => (
              <div key={i} style={{ background: OB.paper, border: `1px solid ${OB.line}`, borderRadius: 14, padding: '20px 22px' }}>
                <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.03em' }}>{m.v}</div>
                <div style={{ fontSize: 13, color: OB.mute, marginTop: 5 }}>{m.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works (near-white, card-less) */}
      <Band surface="paper" id="how-it-works" pad="72px 28px">
        <SectionHeading eyebrow="How it works" title="From work to paid in three steps" sub="Papr handles the parts you’d rather not." />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 28 }} className="onb-feat">
          {[
            { n: '1', t: 'Add your work', d: 'Jot down hours or line items. Papr drafts the invoice from your entries — numbered, dated, and ready.' },
            { n: '2', t: 'Send in a tap', d: 'Review and send. Your invoice goes out branded and professional, from your own business details.' },
            { n: '3', t: 'Get paid, hands-off', d: 'Papr chases politely if it runs late, and reconciles the payment the moment it lands.' },
          ].map((s, i) => (
            <div key={i}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: OB.ink, color: OB.lime, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 700, fontFamily: FONT_MONO, marginBottom: 16 }}>{s.n}</div>
              <div style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em' }}>{s.t}</div>
              <div style={{ fontSize: 14, color: OB.mute, lineHeight: 1.55, marginTop: 8 }}>{s.d}</div>
            </div>
          ))}
        </div>
      </Band>

      {/* Features (beige, near-white cards) */}
      <Band surface="bg" id="features" pad="72px 28px">
        <SectionHeading eyebrow="Why Papr" title="Less busywork, faster cash" sub="The boring parts of getting paid, handled for you." />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }} className="onb-feat">
          {[
            { icon: 'M2.01 21L23 12 2.01 3 2 10l15 2-15 2z', t: 'Drafts that send themselves', d: 'Papr builds invoices from your time entries and contracts, ready to send in one tap.', c: '#0ea5e9', soon: false },
            { icon: 'M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z', t: 'Reminders on autopilot', d: 'Polite, perfectly-timed nudges go out automatically — tuned to how each client pays.', c: '#7c5cff', soon: false },
            { icon: 'M4 10h16v8H4zM4 6h16v2H4z', t: 'Online payments', d: 'Soon, clients will be able to pay by card or bank transfer, with payouts landing in your account automatically.', c: '#10b981', soon: true },
          ].map((f, i) => (
            <div key={i} style={{ background: OB.paper, border: `1px solid ${OB.line}`, borderRadius: 16, padding: '24px 22px' }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: `${f.c}1a`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                <Icon d={f.icon} size={20} color={f.c} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 16.5, fontWeight: 600, letterSpacing: '-0.01em' }}>{f.t}</div>
                {f.soon ? <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: OB.purple, background: 'rgba(124,92,255,0.12)', borderRadius: 999, padding: '2px 8px' }}>Coming soon</span> : null}
              </div>
              <div style={{ fontSize: 13.5, color: OB.mute, lineHeight: 1.55, marginTop: 8 }}>{f.d}</div>
            </div>
          ))}
        </div>
      </Band>

      {/* Mid-page CTA (dark) */}
      <Band surface="ink" pad="64px 28px">
        <div style={{ position: 'relative', overflow: 'hidden', textAlign: 'center', color: '#fff' }}>
          <div style={{ position: 'absolute', top: -140, right: 40, width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(202,255,51,0.14), transparent 65%)' }}></div>
          <div style={{ position: 'absolute', bottom: -160, left: 40, width: 340, height: 340, borderRadius: '50%', background: 'radial-gradient(circle, rgba(124,92,255,0.13), transparent 65%)' }}></div>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <h2 style={{ fontSize: 32, fontWeight: 600, letterSpacing: '-0.03em', margin: 0 }}>Stop chasing. Start getting paid.</h2>
            <p style={{ fontSize: 15.5, color: 'rgba(255,255,255,0.62)', margin: '12px auto 26px', maxWidth: 460 }}>Your first invoice goes out in minutes — Papr quietly handles the follow-ups.</p>
            <a href="/auth/register" className="onb-primary" style={{ height: 50, padding: '0 24px', borderRadius: 13, border: 'none', background: OB.lime, color: OB.ink, fontSize: 15.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 9, textDecoration: 'none' }}>
              Start for free <Icon d="M5 12h14M13 6l6 6-6 6" size={16} color={OB.ink} />
            </a>
          </div>
        </div>
      </Band>

      {/* Proof / testimonials (near-white, beige cards) */}
      <Band surface="paper" pad="72px 28px">
        <SectionHeading eyebrow="From our users" title="Quietly getting people paid" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 16 }} className="onb-feat">
          {[
            { q: 'I haven’t written a “just following up” email in months — Papr does it, and I get paid about a week sooner.', name: 'Mara Ostrowski', role: 'Freelance designer', initial: 'M', c: '#7c5cff' },
            { q: 'Three of us bill from two companies. Papr keeps it all straight, and the reminders alone paid for the year.', name: 'Daniel Reis', role: 'Small-business owner', initial: 'D', c: '#0ea5e9' },
          ].map((t, i) => (
            <figure key={i} style={{ margin: 0, background: OB.bg, border: `1px solid ${OB.line}`, borderRadius: 16, padding: '26px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
              <blockquote style={{ margin: 0, fontSize: 17, lineHeight: 1.5, letterSpacing: '-0.01em', color: OB.ink }}>“{t.q}”</blockquote>
              <figcaption style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 'auto' }}>
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: t.c, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, flexShrink: 0 }}>{t.initial}</div>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: OB.ink }}>{t.name}</div>
                  <div style={{ fontSize: 12.5, color: OB.mute }}>{t.role}</div>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </Band>

      {/* Pricing (beige, near-white cards) */}
      <Band surface="bg" id="pricing" pad="72px 28px">
        <SectionHeading eyebrow="Pricing" title="Simple, honest pricing" sub="Start free. Upgrade when invoicing becomes a real part of your week." />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, alignItems: 'stretch' }} className="onb-feat">
          {[
            { name: 'Starter', price: '€0', cadence: 'free forever', who: 'For getting your first invoices out.', features: ['Up to 5 invoices a month', 'One business', 'Branded PDF invoices', 'Email delivery'], highlight: false },
            { name: 'Pro', price: '€9', cadence: 'per month, billed monthly', who: 'For freelancers who bill regularly.', features: ['Unlimited invoices', 'Automatic reminders', 'Online payments (soon)', 'Recurring invoices (soon)'], highlight: true },
            { name: 'Business', price: '€19', cadence: 'per month, billed monthly', who: 'For teams and multiple businesses.', features: ['Everything in Pro', 'Multiple entities', 'Team seats', 'Priority support'], highlight: false },
          ].map((p, i) => (
            <div key={i} style={{ position: 'relative', background: OB.paper, border: p.highlight ? `1.5px solid ${OB.ink}` : `1px solid ${OB.line}`, borderRadius: 18, padding: '26px 24px', display: 'flex', flexDirection: 'column', boxShadow: p.highlight ? '0 16px 44px rgba(0,0,0,0.10)' : 'none' }}>
              {p.highlight && (
                <span style={{ position: 'absolute', top: 18, right: 18, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, background: OB.lime, color: OB.ink, fontSize: 11, fontWeight: 700, fontFamily: FONT_MONO }}>
                  <Sparkle size={11} color={OB.ink} /> Most popular
                </span>
              )}
              <div style={{ fontSize: 14, fontWeight: 600, color: OB.ink, letterSpacing: '-0.01em' }}>{p.name}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 14 }}>
                <span style={{ fontSize: 38, fontWeight: 600, letterSpacing: '-0.04em', fontFamily: FONT_MONO }}>{p.price}</span>
              </div>
              <div style={{ fontSize: 12, color: OB.dim, fontFamily: FONT_MONO, marginTop: 4 }}>{p.cadence}</div>
              <div style={{ fontSize: 13.5, color: OB.mute, lineHeight: 1.5, marginTop: 12 }}>{p.who}</div>
              <ul style={{ listStyle: 'none', margin: '20px 0 24px', padding: 0, display: 'flex', flexDirection: 'column', gap: 11 }}>
                {p.features.map((f: string) => (
                  <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5, color: OB.ink }}>
                    <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(16,185,129,0.14)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                      <Icon d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" size={11} color={OB.greenInk} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <a href="/auth/register" className={p.highlight ? 'onb-primary' : 'onb-ghost'} style={{ marginTop: 'auto', height: 46, borderRadius: 12, fontSize: 14.5, fontWeight: 600, fontFamily: 'inherit', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', ...(p.highlight ? { background: OB.lime, color: OB.ink, border: 'none' } : { background: '#fff', color: OB.ink, border: `1px solid ${OB.line2}` }) }}>
                Start for free
              </a>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 12.5, color: OB.dim, fontFamily: FONT_MONO, marginTop: 18, textAlign: 'center' }}>Prices in EUR. No card to start.</div>
      </Band>

      {/* FAQ (near-white, centered heading + centered 2-col list) */}
      <Band surface="paper" pad="72px 28px">
        <SectionHeading eyebrow="FAQ" title="Questions, answered" />
        <div style={{ maxWidth: 880, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '8px 40px' }} className="onb-feat">
          {[
            { q: 'Do I need a credit card to start?', a: 'No. Sign up and send your first invoices on the free plan — no card, no trial countdown.' },
            { q: 'Can I import my existing clients and invoices?', a: 'Yes. Bring your history in from CSV, Excel, or JSON, and pick up where you left off.' },
            { q: 'How do my clients pay?', a: 'By card or bank transfer, straight from the invoice. Payouts land in your account automatically.' },
            { q: 'Is my data secure?', a: 'Your data is encrypted, it stays yours, and you can export everything at any time.' },
            { q: 'Can I bill from more than one business?', a: 'Yes. Run multiple entities — each with its own details, branding, and numbering — from one account.' },
            { q: 'I’m not in the US — can I use Papr?', a: 'Yes. Papr is multi-currency and used by people across 40+ countries.' },
          ].map((f, i) => (
            <div key={i} style={{ padding: '20px 0', borderTop: `1px solid ${OB.line}` }}>
              <div style={{ fontSize: 15.5, fontWeight: 600, letterSpacing: '-0.01em', color: OB.ink }}>{f.q}</div>
              <div style={{ fontSize: 14, color: OB.mute, lineHeight: 1.55, marginTop: 8 }}>{f.a}</div>
            </div>
          ))}
        </div>
      </Band>

      {/* Final CTA band (dark card on beige) */}
      <Band surface="bg" pad="64px 28px">
        <div style={{ position: 'relative', overflow: 'hidden', background: OB.ink, borderRadius: 24, padding: '52px 40px', textAlign: 'center', color: '#fff' }}>
          <div style={{ position: 'absolute', top: -120, right: -80, width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle, rgba(202,255,51,0.16), transparent 65%)' }}></div>
          <div style={{ position: 'absolute', bottom: -140, left: -100, width: 380, height: 380, borderRadius: '50%', background: 'radial-gradient(circle, rgba(124,92,255,0.15), transparent 65%)' }}></div>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <h2 style={{ fontSize: 34, fontWeight: 600, letterSpacing: '-0.03em', margin: 0 }}>Send your first invoice today.</h2>
            <p style={{ fontSize: 15.5, color: 'rgba(255,255,255,0.6)', margin: '12px 0 26px' }}>Free to start. No credit card. Set up in about 3 minutes.</p>
            <a href="/auth/register" className="onb-primary" style={{ height: 52, padding: '0 26px', borderRadius: 13, border: 'none', background: OB.lime, color: OB.ink, fontSize: 16, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 9, textDecoration: 'none' }}>
              Start for free <Icon d="M5 12h14M13 6l6 6-6 6" size={17} color={OB.ink} />
            </a>
          </div>
        </div>
      </Band>

      {/* Footer */}
      <SiteFooter />
    </div>
  )
}
